import type { Tag } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { conversationsApi } from 'apis/conversations';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

const MAX_SUGGESTIONS = 3;

type UseSuggestedTagsOptions = {
  /** The conversation id. */
  csid: number;
  /** The tags already applied to the conversation. */
  existingTags: readonly Tag[];
  /** Whether the conversation is open. */
  isOpen: boolean;
  /** Whether the current user is assigned to the conversation. */
  isAssignedToCurrentUser: boolean;
};

/**
 * A hook that returns suggested tags for a conversation.
 *
 * Suggestions are only fetched when:
 * - The feature flag is enabled
 * - No tags are already applied
 * - The conversation is open
 * - The current user is assigned to the conversation
 */
export function useSuggestedTags({
  csid,
  existingTags,
  isOpen,
  isAssignedToCurrentUser,
}: UseSuggestedTagsOptions) {
  const { tagSuggestionsEnabled } = useFeatureFlag(['tag-suggestions-enabled']);

  const shouldFetch =
    tagSuggestionsEnabled &&
    existingTags.length === 0 &&
    isOpen &&
    isAssignedToCurrentUser;

  const { data, isFetching } = conversationsApi.useSuggestConversationTagsQuery(
    { csid },
    { skip: !shouldFetch },
  );

  const suggestions = useMemo(
    () =>
      data
        ?.filter((tag) => !existingTags.some((t) => t.id === tag.id))
        .slice(0, MAX_SUGGESTIONS) ?? [],
    [data, existingTags],
  );

  return {
    suggestions,
    isLoading: isFetching,
    isEnabled: tagSuggestionsEnabled,
  };
}
